"use client";

import { useMemo } from "react";
import {
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { shown } from "@/components/tools/calculus/format";
import {
  FunctionGraph,
  type GraphPoint,
} from "@/components/tools/calculus/function-graph";
import { MathTable } from "@/components/tools/calculus/math-table";
import type { GRAPHING_CALCULATOR_STRINGS } from "@/components/tools/graphing-calculator";
import type { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  findFeatures,
  type GraphFunction,
  valueTable,
} from "@/lib/tool-kit/calculus/graph-model";
import type { Window } from "@/lib/tool-kit/calculus/plot";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Strings = typeof GRAPHING_CALCULATOR_STRINGS;
type Fmt = ReturnType<typeof useToolNumber>;

const TABLE_ROWS = 11;
const LIST_LIMIT = 12;

function color(row: number): string {
  return CHART_COLORS[row % CHART_COLORS.length];
}

function d(fmt: Fmt, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  if (Math.abs(value) < 1e-9) {
    return fmt(0);
  }
  return fmt(value, { maximumSignificantDigits: 7 });
}

export function GraphingView({
  functions,
  window,
  switches,
  tableStart,
  tableStep,
  setTableStart,
  setTableStep,
  strings,
  fmt,
}: {
  functions: GraphFunction[];
  window: Window;
  switches: { zeros: boolean; crossings: boolean; turning: boolean };
  tableStart: string;
  tableStep: string;
  setTableStart: (value: string) => void;
  setTableStep: (value: string) => void;
  strings: Strings;
  fmt: Fmt;
}) {
  const { zeros, crossings, turning } = switches;
  const { xMin, xMax } = window;
  const features = useMemo(
    () => findFeatures(functions, xMin, xMax, { zeros, crossings, turning }),
    [functions, xMin, xMax, zeros, crossings, turning]
  );
  const start = parseNumberInput(tableStart);
  const step = parseNumberInput(tableStep);
  const tableOk = start !== null && step !== null && step !== 0;
  const table = useMemo(
    () => (tableOk ? valueTable(functions, start, step, TABLE_ROWS) : []),
    [functions, tableOk, start, step]
  );
  if (functions.length === 0) {
    return <Notice>{strings.noFunctions}</Notice>;
  }
  const points: GraphPoint[] = [
    ...features.zeros.map((z) => ({ x: z.x, y: 0, color: color(z.row) })),
    ...features.crossings.map((c) => ({
      x: c.x,
      y: c.y,
      color: CHART_COLORS[7],
    })),
    ...features.turning.map((p) => ({
      x: p.x,
      y: p.y,
      color: CHART_COLORS[1],
    })),
  ];
  const name = (row: number) => t(strings.functionName, { n: row + 1 });
  return (
    <>
      <FunctionGraph
        downloadPngLabel={strings.downloadPng}
        downloadSvgLabel={strings.downloadSvg}
        filename="graph"
        points={points}
        series={functions.map((fn) => ({
          f: fn.f,
          color: color(fn.row),
          label: t(strings.seriesLabel, {
            expression: shown(fn.ast, fn.decimals),
          }),
        }))}
        title={t(strings.graphTitle, {
          count: functions.length,
          xMin: d(fmt, window.xMin),
          xMax: d(fmt, window.xMax),
        })}
        window={window}
      />
      {switches.zeros ? (
        <div className="space-y-1">
          <h3 className={WIDGET_H3}>{strings.zerosHeading}</h3>
          {features.zeros.length === 0 ? (
            <p className="text-muted-foreground text-sm">{strings.zerosNone}</p>
          ) : (
            functions.map((fn) => {
              const mine = features.zeros.filter((z) => z.row === fn.row);
              return mine.length === 0 ? null : (
                <p className="text-foreground text-sm" key={fn.row}>
                  {t(strings.zerosLine, {
                    n: fn.row + 1,
                    values: mine
                      .slice(0, LIST_LIMIT)
                      .map((z) => d(fmt, z.x))
                      .join(", "),
                  })}
                  {mine.length > LIST_LIMIT
                    ? ` ${t(strings.more, { count: mine.length - LIST_LIMIT })}`
                    : ""}
                </p>
              );
            })
          )}
        </div>
      ) : null}
      {switches.crossings ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.crossingsHeading}</h3>
          {features.crossings.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {strings.crossingsNone}
            </p>
          ) : (
            <MathTable
              head={[strings.columnPair, strings.columnX, strings.columnY]}
              rows={features.crossings.slice(0, LIST_LIMIT).map((c) => [
                t(strings.pairText, {
                  a: name(c.rows[0]),
                  b: name(c.rows[1]),
                }),
                d(fmt, c.x),
                d(fmt, c.y),
              ])}
            />
          )}
        </div>
      ) : null}
      {switches.turning ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.turningHeading}</h3>
          {features.turning.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {strings.turningNone}
            </p>
          ) : (
            <MathTable
              head={[
                strings.columnFunction,
                strings.columnKind,
                strings.columnX,
                strings.columnY,
              ]}
              rows={features.turning
                .slice(0, LIST_LIMIT)
                .map((p) => [
                  name(p.row),
                  p.kind === "max" ? strings.kindMax : strings.kindMin,
                  d(fmt, p.x),
                  d(fmt, p.y),
                ])}
            />
          )}
        </div>
      ) : null}
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        <div className="grid grid-cols-2 gap-3">
          <TextField
            id="gc-table-start"
            label={strings.tableStartLabel}
            mono
            numeric
            onChange={setTableStart}
            value={tableStart}
          />
          <TextField
            id="gc-table-step"
            label={strings.tableStepLabel}
            mono
            numeric
            onChange={setTableStep}
            value={tableStep}
          />
        </div>
        {tableOk ? (
          <MathTable
            head={[
              strings.tableColumnX,
              ...functions.map((fn) =>
                t(strings.tableColumnFunction, { n: fn.row + 1 })
              ),
            ]}
            rows={table.map((row) => [
              d(fmt, row.x),
              ...row.values.map((v) =>
                Number.isFinite(v) ? d(fmt, v) : strings.undefinedValue
              ),
            ])}
          />
        ) : (
          <ErrorNotice>{strings.tableInvalid}</ErrorNotice>
        )}
      </div>
    </>
  );
}
